//Overview Dashboard//

import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import {
  Users, Building, FolderOpen, TrendingUp, AlertTriangle, Clock, Calendar,
  Trophy, Award, CheckCircle2, ClipboardList, Target
} from 'lucide-react';
import MetricCard from './MetricCard';
import { useNavigate } from 'react-router-dom';

const getRankBadgeStyle = (index) => {
  switch (index) {
    case 0:
      return {
        bg: "bg-gradient-to-br from-amber-400 to-yellow-600 shadow-md shadow-yellow-500/20 text-white",
        icon: <Trophy className="w-4.5 h-4.5 text-white animate-pulse" />
      };
    case 1:
      return {
        bg: "bg-gradient-to-br from-slate-300 to-slate-500 shadow-md shadow-slate-400/20 text-white",
        icon: <Award className="w-4.5 h-4.5 text-white" />
      };
    case 2:
      return {
        bg: "bg-gradient-to-br from-amber-600 to-orange-700 shadow-md shadow-orange-600/20 text-white",
        icon: <Award className="w-4.5 h-4.5 text-white" />
      };
    default:
      return {
        bg: "bg-gradient-to-br from-blue-400 to-indigo-600 text-white",
        icon: <span className="text-xs font-extrabold">{index + 1}</span>
      };
  }
};

const MobileMetricCard = ({ title, value, icon, color }) => {
  const colors = {
    blue: {
      border: 'border-t-blue-500/80',
      bg: 'bg-blue-500/10 text-blue-600',
      glow: 'shadow-blue-500/5',
      edge: 'border-blue-500/10'
    },
    green: {
      border: 'border-t-emerald-500/80',
      bg: 'bg-emerald-500/10 text-emerald-600',
      glow: 'shadow-emerald-500/5',
      edge: 'border-emerald-500/10'
    },
    orange: {
      border: 'border-t-orange-500/80',
      bg: 'bg-orange-500/10 text-orange-600',
      glow: 'shadow-orange-500/5',
      edge: 'border-orange-500/10'
    },
    purple: {
      border: 'border-t-purple-500/80',
      bg: 'bg-purple-500/10 text-purple-600',
      glow: 'shadow-purple-500/5',
      edge: 'border-purple-500/10'
    }
  }[color] || {
    border: 'border-t-slate-400/80',
    bg: 'bg-slate-500/10 text-slate-600',
    glow: '',
    edge: 'border-slate-200'
  };

  return (
    <div className={`bg-white/90 backdrop-blur-md rounded-2xl border-t-4 ${colors.border} border-x border-b ${colors.edge} p-4 shadow-md ${colors.glow} flex flex-col justify-between h-[105px] transition-all active:scale-95`}>
      <div className="flex justify-between items-start">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider line-clamp-1">{title}</span>
        <div className={`p-1.5 rounded-lg ${colors.bg} flex-shrink-0`}>
          {React.cloneElement(icon, { className: 'w-4 h-4' })}
        </div>
      </div>
      <div className="mt-2">
        <span className="text-2xl font-black text-slate-800 tracking-tight font-sans block">{value}</span>
      </div>
    </div>
  );
};

const OverviewDashboard = ({ stats, dashboardData, userRole }) => {
  const navigate = useNavigate();

  // Format trend values
  const formatTrend = (value) => {
    if (value === 0) return '0%';
    return value > 0 ? `+${value}%` : `${value}%`;
  };

  console.log('📊 OverviewDashboard Received Data:', {
    performanceData: dashboardData.performanceData,
    departmentStats: dashboardData.departmentStats,
    recentTickets: dashboardData.recentTickets,
    recentTicketsLength: dashboardData.recentTickets?.length
  });

  return (
    <div className="space-y-4">
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* TOP TASKS PERFORMERS — Desktop (Premium Style) */}
        <div className="hidden md:block bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/60 shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-slate-800 tracking-tight font-sans">Top Tasks Performers</h3>
              <p className="text-xs text-slate-400 mt-0.5">Leading team members by completed tasks</p>
            </div>
            <div className="p-2 bg-amber-50 rounded-xl border border-amber-100">
              <Trophy className="w-5 h-5 text-amber-500" />
            </div>
          </div>
          {dashboardData.performanceData.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.performanceData.map((employee, index) => {
                const badgeStyle = getRankBadgeStyle(index);
                return (
                  <div key={employee.name} className="flex items-center justify-between p-3.5 bg-slate-50/50 hover:bg-white rounded-xl border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all duration-300">
                    <div className="flex items-center space-x-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${badgeStyle.bg}`}>
                        {badgeStyle.icon}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-bold text-slate-700 text-sm">{employee.name}</p>
                          <span className="text-[9px] font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-md">
                            {employee.role || 'Member'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 flex items-center">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mr-1.5" />
                          {employee.completed} {employee.completed === 1 ? 'item' : 'items'} completed
                        </p>
                      </div>
                    </div>
                    {employee.completed > 0 && (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100/50 px-2 py-1 rounded-md">
                        Active
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Award className="w-12 h-12 mx-auto mb-2 text-slate-300 stroke-1" />
              <p className="text-sm font-medium">No performance data available</p>
            </div>
          )}
        </div>

        {/* TOP TASKS PERFORMERS — Mobile (Premium Redesign) */}
        <div className="block md:hidden bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/60 shadow-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-slate-800 tracking-tight font-sans">
                Top Tasks Performers
              </h3>
              <p className="text-[10px] text-slate-400 mt-0.5">Leading team members by completed tasks</p>
            </div>
            <div className="p-1.5 bg-amber-50 rounded-lg border border-amber-100">
              <Trophy className="w-4 h-4 text-amber-500" />
            </div>
          </div>

          {dashboardData.performanceData.length > 0 ? (
            <div className="space-y-1.5">
              {dashboardData.performanceData.map((employee, index) => {
                const badgeStyle = getRankBadgeStyle(index);
                return (
                  <div
                    key={employee.name}
                    className="group flex items-center justify-between px-2.5 py-1.5 bg-slate-50/50 rounded-lg border border-slate-100 hover:border-blue-100 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-2 min-w-0">
                      {/* Rank Badge */}
                      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${badgeStyle.bg}`}>
                        {React.cloneElement(badgeStyle.icon, { className: 'w-3.5 h-3.5' })}
                      </div>

                      <div className="min-w-0 flex flex-col justify-center">
                        <div className="flex items-center space-x-1.5">
                          <span className="font-bold text-slate-700 text-xs truncate">
                            {employee.name}
                          </span>
                          <span className="text-[7.5px] font-semibold bg-slate-100 text-slate-500 px-1 py-0.5 rounded">
                            {employee.role || 'Member'}
                          </span>
                        </div>
                        <p className="text-[9.5px] text-slate-400 flex items-center font-sans mt-px">
                          <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500 mr-1" />
                          {employee.completed} {employee.completed === 1 ? 'item' : 'items'}
                        </p>
                      </div>
                    </div>

                    {employee.completed > 0 && (
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center justify-center text-[8.5px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100/50 px-1 py-0.5 rounded">
                          Active
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Award className="w-10 h-10 mx-auto mb-2 text-slate-300 stroke-1" />
              <p className="text-xs font-medium">No performance data available</p>
            </div>
          )}
        </div>

        {/* UPCOMING DEADLINES */}
        <div className="bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/60 shadow-lg p-6">
          <h3 className="text-lg font-bold text-slate-800 tracking-tight font-sans mb-4">Upcoming Deadlines</h3>
          {dashboardData.upcomingDeadlines.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.upcomingDeadlines.map((item, index) => {
                const deadline = new Date(item.dueDate);
                const today = new Date();
                const daysUntil = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
                return (
                  <div key={index} className="flex items-center justify-between p-3.5 bg-slate-50/50 hover:bg-white border border-slate-100 hover:border-blue-100 hover:shadow-sm rounded-xl transition-all duration-300">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-xl flex-shrink-0 ${daysUntil <= 1 ? 'bg-red-50 text-red-500' : daysUntil <= 3 ? 'bg-amber-50 text-amber-500' : 'bg-blue-50 text-blue-500'}`}>
                        {daysUntil <= 1 ? <AlertTriangle className="w-5 h-5" /> : daysUntil <= 3 ? <Clock className="w-5 h-5" /> : <Calendar className="w-5 h-5" />}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-700 text-sm">{item.title || item.name || item.projectName}</h4>
                        <p className="text-xs text-slate-400 mt-1">{item.type} - {daysUntil === 0 ? 'Due today' : daysUntil < 0 ? 'Overdue' : `Due in ${daysUntil} days`}</p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-md">{deadline.toLocaleDateString('en-GB')}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-slate-400">
              <Calendar className="w-12 h-12 mb-2 text-slate-300 stroke-1" />
              <p className="text-sm font-medium">No upcoming deadlines</p>
            </div>
          )}
        </div>

      </div>

      {/* Performance & Department Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* PROJECT STATUS DISTRIBUTION */}
        <div className="bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/60 shadow-lg p-6">
          <h3 className="text-lg font-bold text-slate-800 tracking-tight font-sans mb-4">Project Status Distribution</h3>
          {dashboardData.projectStatus.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dashboardData.projectStatus}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {dashboardData.projectStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value}`, name]} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value, entry) => `${value} (${entry.payload.value})`}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-400">No project data available</div>
          )}
        </div>

        {/* Recent Tickets */}
        <div className="bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/60 shadow-lg p-6">
          <h3 className="text-lg font-bold text-slate-800 tracking-tight font-sans mb-4">Recent Tickets</h3>
          {dashboardData.recentTickets && dashboardData.recentTickets.length > 0 ? (
            <div className="space-y-3 max-h-[350px] overflow-y-auto">
              {dashboardData.recentTickets.slice(0, 4).map((ticket, index) => (
                <div key={ticket._id || index} className="p-3.5 bg-slate-50/50 hover:bg-white border border-slate-100 hover:border-blue-100 hover:shadow-sm rounded-xl transition-all duration-300">
                  <div className="flex items-start justify-between gap-2 mb-2.5">
                    <h4 className="font-bold text-slate-700 text-sm line-clamp-1 flex-1">{ticket.subject || "No Subject"}</h4>
                    <span className={`px-2.5 py-0.5 text-[9px] font-bold rounded-md uppercase tracking-wider ${ticket.status === 'open' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                      ticket.status === 'inprogress' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                        ticket.status === 'resolved' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                          ticket.status === 'closed' ? 'bg-slate-100 text-slate-600 border border-slate-200' :
                            'bg-orange-50 text-orange-600 border border-orange-100'
                      }`}>
                      {ticket.status || 'Unknown'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="flex items-center text-xs text-slate-500 flex-1 min-w-0 font-sans">
                      <Users className="w-3.5 h-3.5 mr-1.5 text-slate-400 flex-shrink-0" />
                      <span className="truncate">{ticket.customerName || "Unknown"}</span>
                    </div>
                    <div className="flex items-center text-xs text-slate-500 flex-1 min-w-0 font-sans">
                      <Building className="w-3.5 h-3.5 mr-1.5 text-slate-400 flex-shrink-0" />
                      <span className="truncate">{ticket.department || "N/A"}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`px-2 py-0.5 text-[9px] font-bold rounded border ${ticket.priority === 'high' ? 'bg-red-50 text-red-600 border-red-100' :
                      ticket.priority === 'medium' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                        'bg-emerald-50 text-emerald-600 border-emerald-100'
                      }`}>
                      {ticket.priority ? ticket.priority.toUpperCase() : 'LOW'}
                    </span>
                    <span className="text-[10px] font-medium text-slate-400 font-sans">
                      {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-slate-400">
              <FolderOpen className="w-12 h-12 mb-2 text-slate-300 stroke-1" />
              <p className="text-sm">No recent tickets available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverviewDashboard;
